import { PUSH_APP_NAME, PUSH_PLATFORM } from "@/configs/common"

import {
  clearStoredPushDeviceRegistration,
  getStoredPushDeviceRegistration,
} from "@/lib/device-id-storage"
import {
  getDevices,
  unregisterDevice,
  type DeviceResponse,
} from "@/services/devices"

export function isSamePushAppDevice(device: DeviceResponse): boolean {
  return device.app_name === PUSH_APP_NAME && device.platform === PUSH_PLATFORM
}

export function isSamePushDeviceToken(
  device: DeviceResponse,
  token: string
): boolean {
  return device.device_token === token
}

export function isCurrentPushDevice(
  device: DeviceResponse,
  token: string
): boolean {
  return isSamePushAppDevice(device) && isSamePushDeviceToken(device, token)
}

export function findCurrentPushDevice(
  devices: DeviceResponse[],
  token: string
): DeviceResponse | undefined {
  return devices.find((device) => isCurrentPushDevice(device, token))
}

export type { DeviceResponse }

export async function resolveCurrentPushDeviceId(
  address?: string | null
): Promise<string | null> {
  const storedRegistration = getStoredPushDeviceRegistration(address)
  if (storedRegistration.id && storedRegistration.token) {
    return storedRegistration.id
  }
  if (!storedRegistration.token) return null

  try {
    const response = await getDevices()
    const currentDevice = findCurrentPushDevice(
      response?.data ?? [],
      storedRegistration.token
    )
    return currentDevice?.id ?? null
  } catch {
    return null
  }
}

export async function unregisterCurrentPushDevice(
  address?: string | null
): Promise<void> {
  const deviceId = await resolveCurrentPushDeviceId(address)
  if (deviceId) {
    try {
      await unregisterDevice(deviceId)
    } catch {
      // silent
    }
  }
  clearCurrentPushDeviceId(address)
}

export function clearCurrentPushDeviceId(address?: string | null): void {
  clearStoredPushDeviceRegistration(address)
}
